import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { LuBox, LuCalculator } from 'react-icons/lu';

import type { BlockFeature } from '~api/services';
import { PropertyGroup, PropertyItem, TooltipContent, TooltipRoot, TooltipTrigger } from '~components';
import { ScrollArea } from '~components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '~components/ui/sheet';
import { formatNumericWithUnit } from '~utils';
import { BlockHardnessRuleTrace } from './BlockHardnessRuleTrace';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type BlockDetailsProps = {
  /** The block data to display */
  block: BlockFeature | null;
  /** The container element to render the drawer into */
  container?: HTMLElement;
  /** Whether the drawer is open */
  open: boolean;
  /** Callback when the drawer is closed */
  onClose: () => void;
};

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const BLOCK_PARAMETERS: { label: string; getValue: (block: BlockFeature) => string | number }[] = [
  { label: 'strand', getValue: (b) => b.properties.strand ?? '-' },
  { label: 'mineralisation', getValue: (b) => b.properties.mineralisation ?? '-' },
  { label: 'mineralisation_value', getValue: (b) => formatNumericWithUnit(b.properties.mineralisation_value, 0) },
  { label: 'material', getValue: (b) => formatNumericWithUnit(b.properties.material, 0) },
  { label: 'depth', getValue: (b) => formatNumericWithUnit(b.properties.depth, 0) },
  { label: 'density', getValue: (b) => formatNumericWithUnit(b.properties.density, 0) },
  { label: 'geology', getValue: (b) => b.properties.geology ?? '-' },
  { label: 'bf', getValue: (b) => formatNumericWithUnit(b.properties.bf, 0) },
  { label: 'fe', getValue: (b) => formatNumericWithUnit(b.properties.fe, 3) },
  { label: 'lump', getValue: (b) => (b.properties.lump != null ? String(b.properties.lump) : '-') },
  { label: 'maj_min_strand', getValue: (b) => b.properties.maj_min_strand ?? '-' },
  { label: 'mat_hard_vol', getValue: (b) => formatNumericWithUnit(b.properties.mat_hard_vol, 3) },
  { label: 'mat_medium_vol', getValue: (b) => formatNumericWithUnit(b.properties.mat_medium_vol, 3) },
  { label: 'mat_soft_vol', getValue: (b) => formatNumericWithUnit(b.properties.mat_soft_vol, 3) },
  { label: 'mp_dest', getValue: (b) => b.properties.mp_dest ?? '-' },
  { label: 'pit_name', getValue: (b) => b.properties.pit_name ?? '-' },
];

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Sheet component to display details of a selected block, including its hardness and other properties.
 *
 * @example
 * ```tsx
 * <BlockDetailsDrawer
 *   block={selectedBlock}
 *   container={parentContainerRef}
 *   open={isDrawerOpen}
 *   onClose={() => setDrawerOpen(false)}
 * />
 * ```
 */
const BlockDetailsDrawer = ({ block, container, open, onClose }: BlockDetailsProps) => {
  const [showCalculation, setShowCalculation] = useState(false);

  useEffect(() => {
    if (!open) setShowCalculation(false);
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()} modal={false}>
      <SheetContent
        portalContainer={container ?? document.body}
        side="inline-end"
        strategy="absolute"
        showOverlay={false}
        className="grow border-t-0"
        data-open={open}
        aria-describedby={undefined}
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <LuBox className="rounded-full bg-primary/10 p-1 text-primary" size={32} />
            Block Details
          </SheetTitle>
        </SheetHeader>
        <div className="flex min-h-0 flex-1">
          <ScrollArea className="h-full w-full" type="auto">
            {block && (
              <div className="space-y-6 p-6">
                <div
                  className="peer flex items-center justify-between"
                  data-expanded={showCalculation ? 'true' : undefined}
                >
                  <PropertyItem label="Hardness" value={block.properties.hardness} variant="featured" />
                  <TooltipRoot>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-pressed={showCalculation}
                        aria-label="Toggle hardness calculation"
                        className={clsx(
                          'rounded-full p-2 transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2',
                          !showCalculation && 'hover:bg-muted',
                          showCalculation && 'bg-primary text-primary-foreground hover:bg-primary',
                        )}
                        onClick={() => setShowCalculation((current) => !current)}
                      >
                        <LuCalculator color="currentColor" size={24} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left" withPortal={false}>
                      Toggle hardness calculation
                    </TooltipContent>
                  </TooltipRoot>
                </div>

                <div
                  className={clsx(
                    'm-0 h-0 overflow-hidden opacity-0 transition-all duration-300',
                    'peer-data-[expanded=true]:mb-6 peer-data-[expanded=true]:h-auto peer-data-[expanded=true]:opacity-100',
                  )}
                >
                  <BlockHardnessRuleTrace block={block} enabled={open && showCalculation} />
                </div>

                {/* Divider */}
                <div className="h-[1px] rounded-full bg-border" />

                <PropertyGroup>
                  {BLOCK_PARAMETERS.map(({ label, getValue }) => (
                    <PropertyItem key={label} label={label} value={getValue(block)} contained />
                  ))}
                </PropertyGroup>
              </div>
            )}
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { BlockDetailsDrawer };
